import React, { useState, useMemo } from 'react';
import {
  FaEye,
  FaSyncAlt,
  FaCopy,
  FaCode,
  FaExpand,
  FaCompress,
  FaTelegramPlane,
  FaDatabase,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const TELEGRAM_CHAR_LIMIT = 4096;

const formatTelegramMarkdown = (text) => {
  if (!text) return '';
  // Process markdown-like syntax into styled HTML
  let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Bold: **text** or *text* (Telegram uses single * for bold in MarkdownV2, but ** is common)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');
  // Italic: _text_ (single underscore for Telegram markdown)
  html = html.replace(/(?<!\w)\*(.+?)\*(?!\w)/g, '<em class="italic">$1</em>');
  // Code: `text`
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="px-1 py-0.5 rounded bg-black/10 font-mono text-[12px]">$1</code>'
  );
  // Newlines
  html = html.replace(/\n/g, '<br/>');

  return html;
};

const TemplatePreview = ({
  // Primary props (index.js interface)
  template,
  previewData,
  onRefresh,
  // Alternative simple props
  content: propContent,
  loading: propLoading,
}) => {
  const [viewMode, setViewMode] = useState('telegram'); // telegram | rendered | raw
  const [isExpanded, setIsExpanded] = useState(false);
  const [livePreviewData, setLivePreviewData] = useState(null);
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Determine the actual content to display
  const displayContent = useMemo(() => {
    if (livePreviewData?.content) return livePreviewData.content;
    if (propContent !== undefined) return propContent;
    if (previewData?.content) return previewData.content;
    return '';
  }, [livePreviewData, propContent, previewData]);

  const isLoading = propLoading || false;
  const charCount = displayContent?.length || 0;
  const overLimit = charCount > TELEGRAM_CHAR_LIMIT;

  if (!previewData && propContent === undefined) {
    return null;
  }

  const handleCopy = () => {
    if (!displayContent) {
      toast.error('No content to copy');
      return;
    }
    navigator.clipboard
      .writeText(displayContent)
      .then(() => {
        toast.success('Copied to clipboard');
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };

  const handleLivePreview = async () => {
    if (!template?.template_content) {
      toast.error('No template content available');
      return;
    }
    setIsLoadingLive(true);
    try {
      const response = await api.telegramBI.templates.previewWithQueries(
        template.template_content,
        template.template_type || 'text'
      );
      setLivePreviewData(response.data?.data || response.data);
      toast.success('Live preview generated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate live preview');
    } finally {
      setIsLoadingLive(false);
    }
  };

  const renderTelegramView = () => (
    <div className="flex justify-center py-4 px-3">
      <div className="w-full max-w-md">
        {/* Chat header */}
        <div className="flex items-center gap-2 mb-3 px-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
            <FaTelegramPlane className="text-white text-sm" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">
              {template?.template_name || 'Bot Message'}
            </div>
            <div className="text-[10px] text-gray-400">bot</div>
          </div>
          {livePreviewData && (
            <span className="ml-auto px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-600 rounded-full border border-emerald-200">
              Live Data
            </span>
          )}
        </div>

        {/* Chat bubble */}
        <div className="bg-[#efffde] rounded-xl rounded-tl-sm shadow-sm border border-gray-200/50 relative">
          <div className="px-3 pt-2.5 pb-1.5">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : (
              <div
                className="text-[13.5px] leading-[1.35] text-gray-900"
                dangerouslySetInnerHTML={{
                  __html:
                    formatTelegramMarkdown(displayContent) ||
                    '<span class="text-gray-400 italic">No content</span>',
                }}
              />
            )}
          </div>
          {/* Timestamp */}
          <div className="flex items-center justify-end gap-1 px-3 pb-1.5">
            <span className="text-[10px] text-gray-400">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>

        {/* Character count bar */}
        <div className="mt-3 px-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-gray-500">Character count</span>
            <span
              className={`text-[11px] font-mono font-medium ${overLimit ? 'text-red-500' : 'text-gray-600'}`}
            >
              {charCount.toLocaleString()} / {TELEGRAM_CHAR_LIMIT.toLocaleString()}
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                overLimit
                  ? 'bg-red-500'
                  : charCount > TELEGRAM_CHAR_LIMIT * 0.8
                    ? 'bg-amber-400'
                    : 'bg-emerald-400'
              }`}
              style={{ width: `${Math.min(100, (charCount / TELEGRAM_CHAR_LIMIT) * 100)}%` }}
            />
          </div>
          {overLimit && (
            <div className="flex items-center gap-1 mt-1.5 text-[11px] text-red-500">
              <FaExclamationTriangle className="text-[9px]" />
              <span>
                Exceeds Telegram limit by {(charCount - TELEGRAM_CHAR_LIMIT).toLocaleString()}{' '}
                characters. Message will be truncated.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderRenderedView = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
        </div>
      );
    }

    const templateType = template?.template_type || 'text';

    if (templateType === 'html') {
      return (
        <div className="px-4 py-3">
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: displayContent }}
          />
        </div>
      );
    }

    if (templateType === 'markdown') {
      const htmlContent = (displayContent || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\w)\*(.+?)\*(?!\w)/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>')
        .replace(/\n/g, '<br/>');
      return (
        <div className="px-4 py-3">
          <div
            className="prose prose-sm max-w-none text-gray-800"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </div>
      );
    }

    return (
      <pre className="px-4 py-3 whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
        {displayContent || 'No content available'}
      </pre>
    );
  };

  const renderRawView = () => (
    <div className="p-3">
      <pre className="bg-gray-900 dark:bg-gray-50 text-green-400 p-4 rounded-lg overflow-auto text-xs font-mono leading-relaxed max-h-80">
        {displayContent || 'No content available'}
      </pre>
      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[11px] text-gray-400">{charCount.toLocaleString()} characters</span>
        <span className="text-[11px] text-gray-400">Type: {template?.template_type || 'text'}</span>
      </div>
    </div>
  );

  const viewModes = [
    { id: 'telegram', label: 'Telegram', icon: FaTelegramPlane },
    { id: 'rendered', label: 'Rendered', icon: FaEye },
    { id: 'raw', label: 'Raw', icon: FaCode },
  ];

  return (
    <div
      className={`bg-surface rounded-lg border border-gray-200 ${isExpanded ? 'fixed inset-4 z-50 flex flex-col' : ''}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <FaTelegramPlane className="text-[#0088cc] text-sm" />
          <h3 className="text-sm font-semibold text-gray-900">Preview</h3>
          {livePreviewData && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-600 rounded border border-emerald-200">
              Live
            </span>
          )}
          {previewData?.truncated && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-600 rounded border border-amber-200">
              Truncated
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex rounded-md border border-gray-200 overflow-hidden">
            {viewModes.map((mode) => {
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`px-2.5 py-1 text-[11px] font-medium flex items-center gap-1 transition-colors ${
                    viewMode === mode.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface text-gray-500 hover:bg-gray-50'
                  } ${mode.id !== 'telegram' ? 'border-l border-gray-200' : ''}`}
                >
                  <Icon className="text-[9px]" />
                  {mode.label}
                </button>
              );
            })}
          </div>

          {/* Actions */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors border border-gray-200"
              title="Refresh"
            >
              <FaSyncAlt className="text-[11px]" />
            </button>
          )}
          {template?.template_content && (
            <button
              onClick={handleLivePreview}
              disabled={isLoadingLive}
              className="p-1.5 rounded-md text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors border border-emerald-200 disabled:opacity-40"
              title="Live preview with queries"
            >
              {isLoadingLive ? (
                <div className="w-3 h-3 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
              ) : (
                <FaDatabase className="text-[11px]" />
              )}
            </button>
          )}
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors border border-gray-200"
            title="Copy content"
          >
            <FaCopy className="text-[11px]" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors border border-gray-200"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <FaCompress className="text-[11px]" />
            ) : (
              <FaExpand className="text-[11px]" />
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div
        className={`${isExpanded ? 'flex-1 overflow-y-auto' : 'max-h-[400px] overflow-y-auto'} bg-gray-50/30`}
      >
        {viewMode === 'telegram' && renderTelegramView()}
        {viewMode === 'rendered' && renderRenderedView()}
        {viewMode === 'raw' && renderRawView()}
      </div>

      {/* Live preview info bar */}
      {livePreviewData && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-emerald-50/50">
          <div className="flex items-center gap-2 text-[11px] text-emerald-600">
            <FaDatabase className="text-[9px]" />
            <span>Live data from query execution</span>
          </div>
          <button
            onClick={() => setLivePreviewData(null)}
            className="text-[11px] text-emerald-500 hover:text-emerald-700 font-medium transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {isExpanded && (
        <div className="fixed inset-0 bg-black/50 -z-10" onClick={() => setIsExpanded(false)} />
      )}
    </div>
  );
};

export default TemplatePreview;
